import { useState } from "react";

import { formatFileNameForExport } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Column, Row } from "@tanstack/react-table";
import axios from "axios";
import saveAs from "save-as";

import {
    postControlResultsDownloadAttachments,
    postControlResultsExport,
} from "api/generated/control-results/control-results";
import {
    BulkDownloadControlResultAttachmentsErrorPayload,
    ExportControlResultsParams,
} from "api/generated/model";

import { ControlActivityExportModalSubmitType } from "./components/ControlActivityExportModal";
import { buildExportFieldNames } from "./controlActivityExportFields";
import { ControlActivityTableRow } from "./types";

interface UseControlActivityExportUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    visibleColumns: Column<ControlActivityTableRow>[];
}

export function useControlActivityExportUtils({
    selectedRows,
    visibleColumns,
}: UseControlActivityExportUtilsProps) {
    const { t } = useTranslation();
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const exportToolState: TextToolState =
        selectedRows.length > 0
            ? { type: "ENABLED" }
            : {
                  type: "DISABLED",
                  reason: t("ControlActivitiesList_Export_Tooltip"),
              };

    const openExportModal = () => setExportModalOpen(true);
    const closeExportModal = () => setExportModalOpen(false);

    const controlResultIds = selectedRows.map((row) => row.original.activityId);
    const visibleColumnIds = visibleColumns.map((column) => column.id);

    const exportControlResults = async (includeAnswers: boolean) => {
        const params: ExportControlResultsParams = {
            controlResultIds,
            fieldNames: buildExportFieldNames(visibleColumnIds),
            includeAnswers,
            includeLog: includeAnswers,
        };
        const response = await postControlResultsExport(params);
        saveAs(response.data, formatFileNameForExport("activities", "xlsx"));
    };

    const exportAttachments = async () => {
        const response = await postControlResultsDownloadAttachments({
            controlResultIds,
        });
        saveAs(response.data, formatFileNameForExport("attachments", "zip"));
    };

    // On a domain error the response body is a Blob (responseType: "blob"), so we
    // read and parse it to surface the specific archive-size-exceeded message.
    const notifyAttachmentsError = async (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
            try {
                const payload = JSON.parse(
                    await error.response.data.text()
                ) as BulkDownloadControlResultAttachmentsErrorPayload;
                if (payload.type === "ArchiveSizeExceeded") {
                    const maxArchiveSizeInMegabytes = Math.round(
                        payload.value.maxArchiveSize / (1024 * 1024)
                    );
                    emitNotification(
                        "error",
                        t(
                            "ControlResult_BulkDownloadAttachmentsErrorDialog_Body",
                            { maxArchiveSizeInMegabytes }
                        )
                    );
                    return;
                }
            } catch {
                // Fall through to the generic error notification.
            }
        }
        emitNotification("error", t("UnknownError"));
    };

    const handleExport = async (type: ControlActivityExportModalSubmitType) => {
        setIsExporting(true);
        try {
            switch (type) {
                case "ControlActivitiesWithoutAnswers":
                    await exportControlResults(false);
                    break;
                case "ControlActivitiesWithAnswers":
                    await exportControlResults(true);
                    break;
                case "ControlActivityAttachments":
                    await exportAttachments();
                    break;
                default:
                    return assertNever(type);
            }
            closeExportModal();
        } catch (error) {
            if (type === "ControlActivityAttachments") {
                await notifyAttachmentsError(error);
            } else {
                emitNotification("error", t("UnknownError"));
            }
        } finally {
            setIsExporting(false);
        }
    };

    return {
        exportToolState,
        exportModalOpen,
        openExportModal,
        closeExportModal,
        handleExport,
        isExporting,
    };
}
